import { CdkDragEnd } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatSliderChange } from '@angular/material/slider';
import { ActivatedRoute } from '@angular/router';
import { ReplaySubject } from 'rxjs';

import { ScootaversePageService } from 'app/modules/scootaverse/services/scootaverse-page.service';
import { Loggable } from 'app/utils/logging/loggable';
import SocialEntryConfiguration from 'entity/SocialEntryConfiguration';
import EntryFlowType from 'enums/EntryFlowType';
import EntryOptionType from 'enums/EntryOptionType';

@Component({
  selector: 'entry-flow-default-info',
  templateUrl: './entry-flow-default-info.component.html',
  styleUrls: ['./entry-flow-default-info.component.scss'],
  standalone: false,
})
export class EntryFlowDefaultInfoComponent extends Loggable implements OnDestroy, OnInit {
  private destroyed$ = new ReplaySubject<boolean>(1);

  @Input() public settings?: SocialEntryConfiguration;
  @Input() public maxGroupSize = 0;
  @Input() public allowGroupFormers = false;
  @Input() public hasAssignedEntryFlow = false;
  @Output() public settingsChange = new EventEmitter<SocialEntryConfiguration>();
  @Output() public nextStepClick = new EventEmitter<boolean>();

  public scootaverseId?: string;
  protected dragPosition = { x: 165, y: 165 };
  protected dragPositionPercentage = { x: 50, y: 50 };

  protected selectedEntryOption = 'default';
  protected selectedEntryOptionEnum: EntryOptionType = EntryOptionType.DEFAULT;
  protected selectedPattern = 'spiral';
  private selectedEntryFlowType?: EntryFlowType = EntryFlowType.SPIRAL;
  protected groupSizeCountSelected = 1;

  public constructor(
    private route: ActivatedRoute,
    private scootaversePageService: ScootaversePageService,
  ) {
    super();
    this.setDisplayName('EntryFlowDetailsComponent');
  }

  public ngOnInit(): void {
    this.scootaverseId = this.route.snapshot.paramMap.get('id') || undefined;
    if (!this.scootaverseId) {
      void this.scootaversePageService.redirectToHome();
    }

    if (this.settings) {
      this.loadSettings();
    }
  }

  public loadSettings(): void {
    if (this.settings?.entryOption !== undefined) {
      this.selectedEntryOptionEnum = this.settings.entryOption;
      switch (this.selectedEntryOptionEnum) {
        case 0: {
          this.selectedEntryOption = 'default';
          break;
        }
        case 2: {
          this.selectedEntryOption = 'group';
          break;
        }
        case 3: {
          this.selectedEntryOption = 'custom';
          break;
        }
        default: {
          this.selectedEntryOption = 'default';
          break;
        }
      }
    }
    if (this.settings?.groupSpawnSize !== undefined) {
      this.groupSizeCountSelected = this.settings.groupSpawnSize;
    }
    if (this.settings?.entryFlow !== undefined) {
      this.selectedEntryFlowType = this.settings.entryFlow;
      switch (this.selectedEntryFlowType) {
        case 0: {
          this.selectedPattern = 'spiral';
          break;
        }
        case 1: {
          this.selectedPattern = 'grid';
          break;
        }
        default: {
          this.selectedPattern = '';
          break;
        }
      }
    } else {
      this.selectedEntryFlowType = undefined;
      this.selectedPattern = '';
    }
    if (this.settings?.startingPointX !== undefined && this.settings?.startingPointY !== undefined) {
      this.dragPositionPercentage.x = this.settings.startingPointX;
      this.dragPositionPercentage.y = this.settings.startingPointY;

      this.calcDragPosition(this.dragPositionPercentage.x, this.dragPositionPercentage.y);
    }
  }

  public getPattern() {
    if (this.selectedEntryOption !== 'custom') {
      switch (this.selectedPattern) {
        case 'spiral':
          return {
            title: 'Spiral Pattern',
            image: 'spiral-pattern',
          };
        case 'grid':
          return {
            title: 'Grid Pattern',
            image: 'grid-pattern',
          };
        default:
          return {
            title: 'Spiral Pattern',
            image: 'spiral-pattern',
          };
      }
    } else {
      return {
        title: 'Start Point Preview',
        image: 'single-dot',
      };
    }
  }

  public getImagePath() {
    return `assets/images/group-patterns/${this.getPattern().image}.svg`;
  }

  public onGroupSizeChange(event: MatSliderChange): void {
    this.groupSizeCountSelected = Number(event.value);
  }

  public dragEnd($event: CdkDragEnd) {
    const draggedTo = $event.source.getFreeDragPosition();
    this.calcDragPercentage(draggedTo.x, draggedTo.y);
  }

  public calcDragPercentage(x: number, y: number) {
    this.dragPositionPercentage = {
      x: Math.max(0, Math.round((x / 330) * 100)),
      y: Math.max(0, Math.round((y / 330) * 100)),
    };
  }

  public calcDragPosition(x: number, y: number) {
    this.dragPosition = {
      x: Math.max(0, Math.round((x * 330) / 100)),
      y: Math.max(0, Math.round((y * 330) / 100)),
    };
  }

  public resetDragPosition(): void {
    this.dragPosition = { x: 165, y: 165 };
    this.dragPositionPercentage = { x: 50, y: 50 };
  }

  public onClickSettings() {
    void this.scootaversePageService.onScootaverseSettings(this.scootaverseId, true);
  }

  public updateEntryFlowType() {
    switch (this.selectedEntryOption) {
      case 'default':
        this.selectedEntryOptionEnum = EntryOptionType.DEFAULT;
        this.resetDragPosition();
        break;
      case 'group':
        this.selectedEntryOptionEnum = EntryOptionType.CUSTOM_RULES;
        this.resetDragPosition();
        break;
      case 'custom':
        this.selectedEntryOptionEnum = EntryOptionType.STARTING_POINT;
        break;
      default:
        this.selectedEntryOptionEnum = EntryOptionType.DEFAULT;
        break;
    }

    switch (this.selectedPattern) {
      case 'spiral':
        this.selectedEntryFlowType = EntryFlowType.SPIRAL;
        this.resetDragPosition();
        break;
      case 'grid':
        this.selectedEntryFlowType = EntryFlowType.GRID;
        this.resetDragPosition();
        break;
      default:
        this.selectedEntryFlowType = undefined;
        break;
    }
  }

  public nextStep(): void {
    this.settingsChange.emit({
      entryOption: this.selectedEntryOptionEnum,
      groupSpawnSize: this.groupSizeCountSelected,
      entryFlow:
        this.selectedEntryOptionEnum !== EntryOptionType.STARTING_POINT ? this.selectedEntryFlowType : undefined,
      startingPointX: this.selectedEntryFlowType ? undefined : this.dragPositionPercentage.x,
      startingPointY: this.selectedEntryFlowType ? undefined : this.dragPositionPercentage.y,
    });
    this.nextStepClick.emit(true);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
